import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getAllUserNotifications,
  getUnreadUserNotifications,
  readNotification
} from '@/apis/notifications/api';
import { Notification } from '@/apis/notifications/types';

export const useGetAllNotifications = () => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<Notification[], Error>({
    queryKey: ['notifications'],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      return await getAllUserNotifications({ accessToken });
    },
    refetchOnWindowFocus: false
  });
};

export const useReadNotification = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation<void, Error, string[]>({
    mutationFn: async (notificationIds) => {
      const accessToken = await getAccessTokenSilently();
      await Promise.all(
        notificationIds.map((id) => readNotification({ accessToken, updateId: id }))
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  });
};

export const useGetUnreadUserNotifications = () => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<Notification[], Error>({
    queryKey: ['notifications', 'unread'],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      return await getUnreadUserNotifications({ accessToken });
    }
  });
};
